import { useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce, get } from 'lodash';

import {
  fetchOrgPreferences,
  fetchUserInfo,
  fetchUserTeamMember,
  updateCommonUiVisual,
} from '../store/actions/userInfo';
import {
  getHypertestTaskLog_SSE,
  setCurrentRunningLogType,
} from '../store/actions/hypertestTaskLog';
import {
  getHypertestJobDetail_SSE,
  setReportDownloadButtonStatus,
  checkReportIsCreated,
  setArtefectIsOpenStatus,
} from '../store/actions/hypertestJobDetail';
import {
  getHypertestJobDetailToUpdateJobList_SSE,
  insertNewJobIntoJoblist,
} from '../store/actions/hypertestJobList';
import { getHypertestConcurrency } from '../store/actions/concurrency';

import { chunkNumberForSEEEvent, emitEvent } from '../utils/eventEmitter';
import { getHypertestSSEEvent } from '../store/actions/hypertestTasksList';
import { setPromoBannerHeight } from '../store/actions/setting';
import { setCurrentArtefactClick } from 'store/actions/artifacts';

import { Path } from 'constants/routes';
import { sendAmplitudeEvents } from 'utils/common';
import { EventNames } from 'utils/events';

const queryString = require('query-string');

const SSE_Event = {
  CONCURRENCY_UPDATED: 'CONCURRENCY_UPDATED',
  ERROR: 'error',
  JOB_CREATED: 'JOB_CREATED',
  JOB_UPDATED: 'JOB_UPDATED',
  REPORT_UPDATED: 'REPORT_UPDATED',
  STAGE_CREATED: 'STAGE_CREATED',
  STAGE_LOG_AVAILABLE: 'STAGE_LOG_AVAILABLE',
  STAGE_UPDATED: 'STAGE_UPDATED',
  TASK_UPDATED: 'TASK_UPDATED',
};

const SSE_DEBOUNCE_TIME = 100;

// let isSSEEventsStoped = true;
const useApp = () => {
  //let location = useLocation();
  const userInfo = useSelector((state) => state.userInfo?.userInfo);
  let promoBannerHeight =
    useSelector((state) => state.appSetting?.setting?.promoBannerHeight) || 0;

  const orgPreference = useSelector((state) => state.userInfo?.orgPreference);
  // const visual = useSelector((state) => state.userInfo?.visual);
  // const filters = useSelector((state) => state.filters?.filters);
  const hypertestJobList = useSelector(
    (state) => state.hypertestJobList?.hypertestJobList
  );

  let dispatch = useDispatch();
  let intervalRef = useRef('');
  let promoBannerIntervalRef = useRef(0);
  let promoBannerIntervalRetries = useRef(0);
  let jobStageTrack = {};
  let taskSSEIds = useRef({});
  let taskUpdateTime = useRef(new Date().getTime());

  const userId = get(userInfo, ['data', 'id'], undefined);
  const orgData = get(orgPreference, ['data'], undefined);

  const auth = useMemo(() => {
    return {
      username: userInfo?.data?.username,
      password: userInfo?.data?.apiToken,
    };
  }, [userInfo?.data?.username, userInfo?.data?.apiToken]);

  useEffect(() => {
    dispatch(fetchUserInfo()).then((res) => {
      let userData = get(res, 'payload', undefined);
      if (userData) {
        recordUserForRUM(userData);
        let authInfo = {
          username: get(userData, 'username', undefined),
          password: get(userData, 'apiToken', undefined),
        };
        dispatch(
          fetchOrgPreferences({
            orgId: get(userData, ['organization', 'id'], ''),
            auth: authInfo,
          })
        ).then((res) => {
          updateWindowSSEConfig(res);
          dispatch(fetchUserTeamMember(authInfo));
        });
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let source;
    if (!!window.EventSource && userInfo.data && orgPreference.data) {
      // let access_token = getCookieData(cookieName);
      let SSE_URL =
        (window.globalConfig && window.globalConfig.LNS_HOST) ||
        (orgPreference.data && orgPreference.data.LNS_HOST) ||
        process.env.REACT_APP_HYPERTEST_SSE_URL;

      if (window.location.href.includes('stage-eu')) {
        SSE_URL = 'https://stage-eu-hyperexecute-api.lambdatestinternal.com';
      }
      if (
        (window.globalConfig && window.globalConfig.LNS_HOST) ||
        (orgPreference.data && orgPreference.data.LNS_HOST)
      ) {
        source = new EventSource(
          `${SSE_URL}/api/v1/sse/hyperexecute?key=${btoa(
            userInfo.data.username + ':' + userInfo.data.apiToken
          )}`
        );
      } else {
        source = new EventSource(`${SSE_URL}/api/v1/sse/hyperexecute`, {
          withCredentials: true,
        });
      }

      source.addEventListener(
        SSE_Event.STAGE_CREATED,
        handleStageCreated,
        false
      );
      source.addEventListener(
        SSE_Event.STAGE_UPDATED,
        handleStageUpdated,
        false
      );
      source.addEventListener(SSE_Event.TASK_UPDATED, handleTaskUpdated, false);
      source.addEventListener(SSE_Event.JOB_CREATED, handleJobCreated, false);
      source.addEventListener(
        SSE_Event.JOB_UPDATED,
        debounce(handleJobUpdated, SSE_DEBOUNCE_TIME),
        false
      );
      source.addEventListener(
        SSE_Event.CONCURRENCY_UPDATED,
        handleConcurrencyUpdated,
        false
      );
      source.addEventListener(
        SSE_Event.STAGE_LOG_AVAILABLE,
        handleStageLogAvailable,
        false
      );
      source.addEventListener(
        SSE_Event.REPORT_UPDATED,
        handleReportUpdated,
        false
      );
      source.addEventListener(SSE_Event.ERROR, handleSSEError, false);
    } else {
      // console.log('Your browser does not support SSE');
    }
    // Handling manual update if SSE is not working

    if (userInfo.data) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      intervalRef.current = setInterval(() => {
        // const parsed = queryString.parse(window.location.search);
        // update concurrency
        dispatch(
          getHypertestConcurrency({
            auth,
            orgId: get(userInfo, ['data', 'organization', 'id'], ''),
          })
        );
      }, 30000);
    }
    if (userInfo.data) {
      const parsed = queryString.parse(window.location.search);
      let framework = parsed && parsed.framework;
      if (!framework) {
        let state = window.location.href.split('state=');
        if (state && state.length > 1) {
          state = state[1].split('&');
          state = state && state.length && state[0];
        }
        let last_url = decodeURIComponent(state);
        if (last_url) {
          framework = last_url.split('framework=');
          framework = framework && framework.length && framework[1];
        }
      }
      if (framework) {
        dispatch(
          updateCommonUiVisual({
            continueToGitpodModal: true,
            frameworkName: framework,
          })
        );
      }
      if (parsed?.isArtifactOpen === 'true') {
        dispatch(setArtefectIsOpenStatus(true));
      }
      if (parsed?.selectedArtifact) {
        dispatch(setCurrentArtefactClick({ name: parsed?.selectedArtifact }));
      }
    }
    window.addEventListener('TopPromoInitialized', handleTopPromoBanner);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (source) {
        console.info('Closing connection');
        source.close();
      }
      window.removeEventListener('TopPromoInitialized', handleTopPromoBanner);
      document.removeEventListener('closeTopPromo', closeTopPromo);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orgData]);

  useEffect(() => {
    promoBannerIntervalRef.current = setInterval(intervalForPromoBanner, 500);

    return function () {
      clearInterval(promoBannerIntervalRef.current);
      promoBannerIntervalRetries.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetPromoIntervalAndRetry() {
    clearInterval(promoBannerIntervalRef.current);
    promoBannerIntervalRetries.current = 0;
  }

  function recordUserForRUM({ id: userId = '', organization = {} } = {}) {
    const { id: orgId = '', subscription_type = '' } = organization;
    if (window.sumoLogicOpenTelemetryRum) {
      window.sumoLogicOpenTelemetryRum.setDefaultAttribute('userId', userId);
      window.sumoLogicOpenTelemetryRum.setDefaultAttribute('orgId', orgId);
      window.sumoLogicOpenTelemetryRum.setDefaultAttribute(
        'subscriptionType',
        subscription_type
      );
    }
  }

  function intervalForPromoBanner() {
    if (promoBannerIntervalRetries.current >= 7) {
      resetPromoIntervalAndRetry();
    } else {
      const bannerElCloseIcon = document.getElementById('close_top_promo');
      if (bannerElCloseIcon && promoBannerHeight <= 0) {
        document.removeEventListener('closeTopPromo', closeTopPromo);
        dispatch(setPromoBannerHeight(42));
        document.addEventListener('closeTopPromo', closeTopPromo);
        resetPromoIntervalAndRetry();
      } else if (promoBannerHeight > 0) {
        resetPromoIntervalAndRetry();
      } else {
        promoBannerIntervalRetries.current =
          promoBannerIntervalRetries.current + 1;
      }
    }
  }

  function handleTopPromoBanner() {
    const bannerElCloseIcon = document.getElementById('close_top_promo');
    if (bannerElCloseIcon) {
      document.removeEventListener('closeTopPromo', closeTopPromo);
      dispatch(setPromoBannerHeight(42));
      document.addEventListener('closeTopPromo', closeTopPromo);
    }
  }

  function closeTopPromo() {
    sendAmplitudeEvents(EventNames.HYP_TOP_BANNER_CLOSE_BUTTON);
    dispatch(setPromoBannerHeight(0));
  }

  function updateWindowSSEConfig(res) {
    if (res && res.payload) {
      window.globalConfig = {};
      if (res.payload.SENTINEL_HOST) {
        window.globalConfig.SENTINEL_HOST = res.payload.SENTINEL_HOST;
      }
      if (res.payload.ORCHESTRATOR_HOST) {
        window.globalConfig.ORCHESTRATOR_HOST = res.payload.ORCHESTRATOR_HOST;
      }
      if (res.payload.LOGISTICS_HOST) {
        window.globalConfig.LOGISTICS_HOST = res.payload.LOGISTICS_HOST;
      }
      if (res.payload.LNS_HOST) {
        window.globalConfig.LNS_HOST = res.payload.LNS_HOST;
      }
      if (res.payload.DISABLE_CDN_LOGS) {
        window.globalConfig.DISABLE_CDN_LOGS = res.payload.DISABLE_CDN_LOGS;
      }
    }
  }

  function isHomePage() {
    return window.location.pathname.includes(Path.JOBS);
  }

  function handleStageCreated(e) {
    if (!isHomePage()) {
      const parsed = queryString.parse(window.location.search);
      let data = JSON.parse(e.data);
      jobStageTrack[data.jobId] = jobStageTrack[data.jobId]
        ? jobStageTrack[data.jobId] + 1
        : 1;
      if (jobStageTrack[data.jobId] === 1 && parsed.jobId === data.jobId) {
        dispatch(
          getHypertestJobDetail_SSE({
            auth,
            jobId: data.jobId,
          })
        );
        // @todo - verify with Ravi. Now we will not depend upon Tasks coming from job details.
        // .then((res) => {
        //   console.log('hellow')
        //   if (get(res, ['payload', 'data', 'Tasks'], []).length) {
        //     if (parsed.taskId) {
        //       // history.push(
        //       //   `?jobId=${parsed.jobId}&taskId=${parsed.taskId}`
        //       // );
        //     } else {
        //       history.push(
        //         `?jobId=${parsed.jobId}&taskId=${res.payload.data.Tasks[0].id}`
        //       );
        //     }
        //   }
        // });
      }
      if (
        (data.taskId === parsed.taskId || !parsed.taskId) &&
        parsed.jobId === data.jobId
      ) {
        dispatch(
          getHypertestTaskLog_SSE({
            auth,
            taskId: data.taskId,
            search_text: parsed?.scenario_search_text || '',
          })
        );
      }
    }
  }

  function handleStageUpdated(e) {
    if (!isHomePage()) {
      const parsed = queryString.parse(window.location.search);
      let dataTask = JSON.parse(e.data);
      let dataStage = JSON.parse(e.data);
      delete jobStageTrack[dataStage.jobId];
      if (dataTask.taskId === parsed.taskId) {
        dispatch(
          getHypertestTaskLog_SSE({
            auth,
            taskId: dataStage.taskId,
            search_text: parsed?.scenario_search_text || '',
          })
        ).then((response) => {
          if (get(response, ['payload', 'data'], []).length) {
            let logsData = response.payload.data.filter((log, ind) => {
              return log.status === 'in-progress';
            });
            if (logsData && logsData.length) {
              dispatch(setCurrentRunningLogType(logsData[0].type));
            } else {
              dispatch(setCurrentRunningLogType('-'));
            }
          } else {
            dispatch(setCurrentRunningLogType('-'));
          }
        });
      }
    }
  }

  function handleTaskUpdated(e) {
    if (!isHomePage()) {
      const prevIds = taskSSEIds.current;

      console.log('* prevIds-', prevIds);

      if (prevIds[e.taskId]) {
        console.log('* Clearing timeout for', e.taskId);
        clearTimeout(prevIds[e.taskId]);
      }

      //  console.log('** Task Updated for ', e?.taskId);

      prevIds[e.taskId] = setTimeout(() => {
        const parsed = queryString.parse(window.location.search);
        let dataTask = JSON.parse(e.data);
        if (parsed.jobId === dataTask.jobId) {
          dispatch(
            getHypertestJobDetail_SSE({
              auth,
              jobId: dataTask.jobId,
            })
          );
          dispatch(
            getHypertestSSEEvent({
              eventData: dataTask,
            })
          );
        }
        if (dataTask.taskId === parsed.taskId) {
          dispatch(
            getHypertestTaskLog_SSE({
              auth,
              taskId: dataTask.taskId,
              search_text: parsed?.scenario_search_text || '',
            })
          );
        }
      }, 100);
      taskSSEIds.current = prevIds;
    }

    const currentTime = new Date().getTime();
    const timeDiff = (currentTime - taskUpdateTime.current) / 1000;
    if (timeDiff >= 2) {
      taskUpdateTime.current = currentTime;
      dispatch(
        getHypertestConcurrency({
          auth,
          orgId: get(userInfo, ['data', 'organization', 'id'], ''),
        })
      );
    } else {
      taskUpdateTime.current = currentTime;
    }
  }

  async function handleJobCreated(e) {
    if (isHomePage()) {
      let dataTask = JSON.parse(e.data);

      const result = await dispatch(
        getHypertestJobDetailToUpdateJobList_SSE({
          auth,
          jobId: dataTask.jobId,
        })
      );
      if (
        result.payload.data &&
        hypertestJobList &&
        !hypertestJobList.loading
      ) {
        dispatch(insertNewJobIntoJoblist({ data: result.payload.data }));
      }
    }
    if (window.location.href.indexOf('quickstart') !== -1) {
      dispatch(
        updateCommonUiVisual({
          onboardingJobCreatedSSElisten: true,
        })
      );
    }
  }

  async function handleJobUpdated(e) {
    if (isHomePage()) {
      let dataTask = JSON.parse(e.data);

      const result = await dispatch(
        getHypertestJobDetailToUpdateJobList_SSE({
          auth,
          jobId: dataTask.jobId,
        })
      );
      const needToInsert =
        result?.payload?.data && hypertestJobList && !hypertestJobList.loading;
      if (needToInsert) {
        dispatch(
          insertNewJobIntoJoblist({
            data: result.payload.data,
            isUpdateEvent: true,
          })
        );
      }
    }

    if (!isHomePage()) {
      let dataTask = JSON.parse(e.data);
      const parsed = queryString.parse(window.location.search);
      if (parsed.jobId === dataTask.jobId && dataTask.status === 'aborted') {
        dispatch(
          getHypertestJobDetail_SSE({
            auth,
            jobId: dataTask.jobId,
          })
        );
        dispatch(
          getHypertestTaskLog_SSE({
            auth,
            taskId: parsed.taskId,
            search_text: parsed?.scenario_search_text || '',
          })
        );
      }
    }
  }

  function handleConcurrencyUpdated(e) {
    dispatch(
      getHypertestConcurrency({
        auth,
        orgId: get(userInfo, ['data', 'organization', 'id'], ''),
      })
    );
  }

  function handleStageLogAvailable(e) {
    if (!isHomePage()) {
      let dataTask = JSON.parse(e.data);
      const parsed = queryString.parse(window.location.search);
      let splitChunk = dataTask.link.split('.');
      if (
        (parseInt(parsed.order) === dataTask.order ||
          dataTask.chunk.indexOf('pre') !== -1 ||
          dataTask.chunk.indexOf('post') !== -1) &&
        dataTask.taskId === parsed.taskId &&
        (splitChunk[0] === 'chunk' ||
          splitChunk[0] === 'pre' ||
          splitChunk[0] === 'post')
      ) {
        setTimeout(() => {
          emitEvent('newChunk', {
            auth,
            chunkNumber: chunkNumberForSEEEvent,
            chunkRetry: splitChunk[2],
            isSSEUpdate: true,
            iteration: dataTask.iteration ? dataTask.iteration : 0,
            logType: parsed.logType,
            order: dataTask.order,
            stageNumber: splitChunk[1],
            taskId: dataTask.taskId,
          });
        }, 600);
      }
      // TODO - after testing remove it. It is not clear if this is being used
      // if (
      //   parseInt(parsed.order) === dataTask.order &&
      //   dataTask.taskId === parsed.taskId &&
      //   (splitChunk[0] === 'pre' || splitChunk[0] === 'post')
      // ) {
      //   dispatch(
      //     stageLogByChunk({
      //       auth,
      //       iteration: dataTask.iteration,
      //       link: dataTask.link,
      //       logType: parsed.logType,
      //       taskId: dataTask.taskId,
      //     })
      //   );
      // }
    }
  }

  function handleReportUpdated(e) {
    if (!isHomePage()) {
      const parsed = queryString.parse(window.location.search);
      let jobReportData = JSON.parse(e.data);
      if (parsed.jobId === jobReportData.jobId) {
        dispatch(setReportDownloadButtonStatus(jobReportData.status));
        dispatch(
          checkReportIsCreated({
            auth,
            jobId: parsed.jobId,
          })
        );
      }
    }
  }

  function handleSSEError(e) {
    console.error('**SSE ERROR** \n', e);
    // if (e.eventPhase === EventSource.CLOSED) source.close();
    // if (e.target.readyState === EventSource.CLOSED) {
    //   // console.log('Disconnected');
    // } else if (e.target.readyState === EventSource.CONNECTING) {
    //   // console.log('Connecting...');
    // }
  }

  return {
    userInfo,
    orgPreference,
  };
};

export default useApp;
